import {
  FirebaseProviderSummary,
  InviteUser,
  ProviderCapacity,
  ProviderSummary,
  ProviderUpdate,
  RoleSummary,
} from "@/types/provider";
import { Tag, TagCreate } from "@/types/tag";

import { requestData } from "@/api/api-config";

export async function getProvider(
  providerId: string,
): Promise<ProviderSummary | null> {
  return requestData<ProviderSummary>(`admin/providers/${providerId}`);
}

export async function updateProvider(
  providerId: string,
  providerUpdate: ProviderUpdate,
  catchError: boolean = true,
): Promise<ProviderSummary | null> {
  return requestData<ProviderSummary>(
    `admin/providers/${providerId}`,
    "put",
    { json: providerUpdate },
    catchError,
  );
}

export async function listRoles(): Promise<RoleSummary[] | null> {
  return requestData<RoleSummary[]>("admin/providers/role/list");
}

export async function listProviders(
  locationId?: string,
  permission?: string,
): Promise<FirebaseProviderSummary[] | null> {
  return requestData<FirebaseProviderSummary[]>("admin/providers/list", "get", {
    searchParams: {
      locationId,
      permission,
    },
  });
}

export async function listProviderCapacity(): Promise<
  ProviderCapacity[] | null
> {
  return requestData<ProviderCapacity[]>("admin/providers/capacity/list");
}

export async function inviteProvider(
  inviteUser: InviteUser,
): Promise<ProviderSummary | null> {
  return await requestData<ProviderSummary>(
    "admin/providers/invite",
    "post",
    {
      json: inviteUser,
    },
    false,
  );
}
export async function changeProviderCapacity(
  providerId: string,
  capacity: number,
): Promise<boolean | null> {
  return await requestData<boolean | null>(
    `admin/providers/${providerId}/capacity?capacity=${capacity}`,
    "put",
  );
}

export async function resendProviderInvite(providerId: string): Promise<void> {
  await requestData<boolean>(
    `admin/providers/${providerId}/resend`,
    "post",
    undefined,
    false,
  );
}

export async function generateProviderInviteLink(
  providerId: string,
): Promise<string | null> {
  const response = await requestData<{ magicLink: string }>(
    `admin/providers/${providerId}/invite-link`,
    "post",
  );
  return response?.magicLink ?? null;
}

export async function archiveProvider(providerId: string): Promise<boolean> {
  return (
    (await requestData<boolean>(
      `admin/providers/${providerId}/archive`,
      "put",
    )) !== null
  );
}

export async function updateProviderRoles(
  providerId: string,
  roles: string[],
): Promise<boolean> {
  return (
    (await requestData<boolean>(`admin/providers/${providerId}/roles`, "put", {
      json: { provider_id: providerId, roles: roles },
    })) !== null
  );
}

export async function updateProviderPrimaryLocation(
  providerId: string,
  primaryLocationId: string | null,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `admin/providers/${providerId}/location`,
    "put",
    {
      searchParams: { primaryLocationId },
    },
    false,
  );
}

export async function getProviderVisibleLocations(
  providerId: string,
): Promise<string[] | null> {
  return requestData<string[]>(
    `admin/providers/${providerId}/visible-locations`,
  );
}

export async function updateProviderVisibleLocations(
  providerId: string,
  locationIds: string[],
): Promise<boolean> {
  return (
    (await requestData<boolean>(
      `admin/providers/${providerId}/visible-locations`,
      "put",
      {
        json: { location_ids: locationIds },
      },
    )) !== null
  );
}

export async function listTags(): Promise<Tag[] | null> {
  return requestData<Tag[]>("admin/tags");
}

export async function createRole(name: string): Promise<RoleSummary | null> {
  return await requestData<RoleSummary>(`admin/role/${name}`, "post");
}

export async function createTag(tag: TagCreate): Promise<boolean | null> {
  return await requestData<boolean>("admin/tags", "post", {
    json: tag,
  });
}

export async function updateTag(
  tagId: string,
  tag: TagCreate,
): Promise<boolean | null> {
  return await requestData<boolean>(`admin/tags/${tagId}`, "put", {
    json: tag,
  });
}

export async function deleteTag(tagId: string): Promise<boolean | null> {
  return await requestData<boolean>(`admin/tags/${tagId}`, "delete");
}

export async function deleteRole(roleId: string): Promise<boolean | null> {
  return await requestData<boolean>(`admin/roles/${roleId}`, "delete");
}

export async function updateProviderProfileImage(
  providerId: string,
  file?: File | null,
): Promise<string | null> {
  const options: { body?: FormData } = {};

  if (file) {
    // Upload new profile image
    const formData = new FormData();
    formData.append("file", file);
    options.body = formData;
  }

  // If no file, request will be sent with no body to remove the image.
  return await requestData<string>(
    `admin/providers/${providerId}/profile-image`,
    "post",
    options,
  );
}

export async function updateProviderSignature(
  providerId: string,
  file?: File | null,
): Promise<string | null> {
  const options: { body?: FormData } = {};

  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    options.body = formData;
  }

  // No file removes the adopted signature.
  return await requestData<string>(
    `admin/providers/${providerId}/signature`,
    "post",
    options,
  );
}

// Onboarding API functions
export type OnboardingStep =
  | "profile"
  | "theme"
  | "calendarSync"
  | "calendarView"
  | "notifications"
  | "security";

export async function completeOnboardingStep(
  providerId: string,
  step: OnboardingStep,
): Promise<boolean> {
  return (
    (await requestData<boolean>(
      `admin/providers/${providerId}/onboarding/${step}/complete`,
      "put",
    )) !== null
  );
}

export async function revertOnboardingStep(
  providerId: string,
  step: OnboardingStep,
): Promise<boolean> {
  return (
    (await requestData<boolean>(
      `admin/providers/${providerId}/onboarding/${step}/revert`,
      "put",
    )) !== null
  );
}

export async function skipOnboarding(providerId: string): Promise<boolean> {
  return (
    (await requestData<boolean>(
      `admin/providers/${providerId}/onboarding/skip`,
      "put",
    )) !== null
  );
}

export async function reorderProviders(
  orders: { id: string; selfScheduleDisplayOrder: number }[],
): Promise<boolean | null> {
  return requestData<boolean>("admin/providers/reorder", "put", {
    json: { orders },
  });
}
